import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useMemo, useRef, useEffect } from 'react';
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui.esm.js';
import isReact from '../../utils/is-react.esm.js';
import { toNumber } from '../../utils/number.esm.js';
import { get } from '../../utils/object.esm.js';
import { getPaddingY, getPaddingX, containsOmitPropSymbol, getSpace, omitSpace } from '../../utils/props-utils.esm.js';
import { resolveSize } from '../../utils/size-utils.esm.js';
import { FlexPrivate } from '../_primitives/flex/flex.esm.js';
import { FlexInlinePrivate } from '../_primitives/flex/flex-inline.esm.js';
import Text from '../_primitives/text/text.esm.js';

var _excluded = ["display", "type", "variant", "anchorOrigin", "badgeContent", "max", "showZero", "overlap", "as", "invisible", "children", "size", "borderRadius", "sx", "enableClickableStyles"];
function usePreviousProps(value) {
  var ref = useRef({});
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
}
var BadgeIndicator = /*#__PURE__*/React.forwardRef(function BadgeIndicator(props, ref) {
  // @ts-ignore
  return /*#__PURE__*/React.createElement(FlexPrivate, _extends({
    ref: ref
  }, props));
});
var Badge = /*#__PURE__*/React.forwardRef(function Badge(props, ref) {
  var theme = useSafeThemeUI(props);
  var display = props.display,
    _props$type = props.type,
    typeProp = _props$type === void 0 ? 'standard' : _props$type,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'default' : _props$variant,
    _props$anchorOrigin = props.anchorOrigin,
    anchorOriginProp = _props$anchorOrigin === void 0 ? {
      vertical: 'top',
      horizontal: 'right'
    } : _props$anchorOrigin,
    badgeContentProp = props.badgeContent,
    _props$max = props.max,
    maxProp = _props$max === void 0 ? 99 : _props$max,
    _props$showZero = props.showZero,
    showZero = _props$showZero === void 0 ? false : _props$showZero,
    _props$overlap = props.overlap,
    overlapProp = _props$overlap === void 0 ? 'rectangular' : _props$overlap,
    _props$as = props.as,
    asProps = _props$as === void 0 ? 'span' : _props$as,
    invisibleProp = props.invisible,
    children = props.children,
    sizeProps = props.size,
    _props$borderRadius = props.borderRadius,
    borderRadius = _props$borderRadius === void 0 ? 'pill' : _props$borderRadius,
    sx = props.sx,
    enableClickableStyles = props.enableClickableStyles,
    rest = _objectWithoutProperties(props, _excluded);
  var size = !sizeProps && children ? 'md' : undefined;
  var _fontSize = resolveSize(size, get(theme, 'sizes.font'));
  var combinePadding = _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, getPaddingX(props)), getPaddingY(props)), getPaddingX(sx)), getPaddingY(sx));
  var paddingProps = containsOmitPropSymbol(combinePadding) || isReact.element(badgeContentProp) ? {} : _objectSpread2({
    emPaddingX: 'xs-2'
  }, combinePadding);
  var prevProps = usePreviousProps({
    anchorOrigin: anchorOriginProp,
    badgeContent: badgeContentProp,
    max: maxProp,
    overlap: overlapProp,
    type: typeProp
  });
  var invisible = invisibleProp;
  if (invisibleProp == null && (badgeContentProp === 0 && !showZero || badgeContentProp == null && typeProp !== 'dot')) {
    invisible = true;
  }
  var _ref = invisible ? prevProps : props,
    _ref$anchorOrigin = _ref.anchorOrigin,
    anchorOrigin = _ref$anchorOrigin === void 0 ? anchorOriginProp : _ref$anchorOrigin,
    badgeContent = _ref.badgeContent,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? maxProp : _ref$max,
    _ref$overlap = _ref.overlap,
    overlap = _ref$overlap === void 0 ? overlapProp : _ref$overlap,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? typeProp : _ref$type;
  var displayValue = '';
  if (type !== 'dot') {
    displayValue = toNumber(badgeContent) > max ? "".concat(max, "+") : badgeContent;
  }
  var spaceProps = getSpace(rest);
  var textProps = useMemo(function () {
    return {
      fontSize: !children ? '0.75em' : '0.6em',
      sx: _objectSpread2({}, enableClickableStyles ? {
        cursor: 'pointer',
        '-webkit-user-select': 'none' /* Safari */,
        '-ms-user-select': 'none' /* IE 10 and IE 11 */,
        'user-select': 'none' /* Standard syntax */
        // '&:hover': {
        //   /* Apply this to BadgeIndicator ONLY(not needed atm) */
        //   opacity: 0.45
        // }
      } : null)
    };
  }, [children, enableClickableStyles]);
  return /*#__PURE__*/React.createElement(FlexInlinePrivate, _extends({
    display: display,
    ref: ref,
    as: asProps,
    relative: true,
    alignItems: "center",
    flexShrink: 0,
    className: 'csg-tui-badge',
    fontSize: _fontSize,
    width: 'fit-content'
  }, !children && invisible ? {
    display: 'none'
  } : spaceProps), children || /*#__PURE__*/React.createElement(FlexInlinePrivate, {
    as: "span",
    color: "transparent",
    maxHeight: ".8em",
    emPaddingX: "xs-3"
  }, toNumber(displayValue) < 10 ? "0".concat(displayValue) // need a 2 digit placeholder
  : displayValue), /*#__PURE__*/React.createElement(BadgeIndicator, _extends({
    as: "span",
    __themeKey: "badges",
    variant: variant,
    className: classNames({
      'csg-tui-badge-indicator': true,
      dot: type === 'dot',
      invisible: invisible
    }),
    sx: textProps.sx,
    anchorOrigin: !!children && anchorOrigin,
    overlap: overlap,
    invisible: invisible,
    absolute: true,
    fontWeight: "body",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    zIndex: 50 // Render the badge on top of potential ripples.
    ,
    borderRadius: borderRadius
  }, _objectSpread2(_objectSpread2({}, !children && invisible ? {} : _objectSpread2(_objectSpread2({
    minWidth: !children ? '1.3em' : '1em',
    height: !children ? '1.3em' : '1em'
  }, !children ? {
    top: '-35%',
    left: '10%'
  } : {}), paddingProps)), omitSpace(rest))), !isReact.element(displayValue) ? /*#__PURE__*/React.createElement(Text, textProps, displayValue) : displayValue));
});

export default Badge;
//# sourceMappingURL=badge.esm.js.map
